// Wishlist API — typed wrappers for /api/v1/wishes/* (W33.1 frontend port).
//
// Backend ships 31 endpoints under `/api/v1/wishes` per W33.0 (see
// `backend/app/modules/wishlist/__init__.py::MANIFEST` + ADR-0025). This
// client wires the surface needed for the W33.1 kanban route; later
// sprints (W33.2 mobile, W33.3 layout editor) will extend.
//
// Per the pattern used in `src/lib/api/diary.ts` + `src/lib/api/pair.ts`:
//   - `api` re-export from `./client` (openapi-fetch with CSRF + cookie)
//   - Types are aliases onto the generated contract where identical to it
//     (`schema.ts`), hand-written where not, each with a recorded reason in
//     `schema-parity.test.ts`
//   - `ApiError` is the standard `app.core.errors` envelope shape
//
// Schema authority: `backend/app/modules/wishlist/schemas.py` (Pydantic).
// If a field drifts, the openapi.yaml regen + `pnpm dev:check-schema`
// (per AGENTS.md §3 #3) will catch it on CI.

import { api as openapi } from "./client";
import { ApiError } from "./pair";
import { assertResponseBody } from "./emptyBody";
import type {
  LinkedDiarySummary as SchemaLinkedDiarySummary,
  WishChatMessageCreate as SchemaWishChatMessageCreate,
  WishChatMessageUpdate as SchemaWishChatMessageUpdate,
  WishTimelineItemCreate as SchemaWishTimelineItemCreate,
  WishTimelineItemOut as SchemaWishTimelineItemOut,
  WishTrashItem as SchemaWishTrashItem,
} from "./schema";

///: Shown when a wishlist response carried no `error.message` to display.
const WISHLIST_REQUEST_FAILED = "心願操作未能完成，請稍後再試。";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyClient = Record<"GET" | "POST" | "PUT" | "DELETE" | "PATCH", any>;
const api = openapi as unknown as AnyClient;

interface CallOptions {
  body?: unknown;
  headers?: Record<string, string>;
  /** openapi-fetch reads query params from `params.query` — see diary.ts. */
  params?: { query: Record<string, unknown> };
  /**
   * Set `false` only on a route whose OpenAPI contract declares no 2xx body
   * (every one of them answers `204`). A 2xx that arrives empty on any other
   * route is a broken server and must reach `isError` — see `emptyBody.ts`.
   */
  expectBody?: boolean;
}

/**
 * The single call funnel for this module: CSRF + cookie via `./client`, bodyless
 * 2xx guard, and the `ApiError` envelope every caller catches.
 *
 * Exported for `./wishBlocks`, which would otherwise have to re-implement it —
 * and a second funnel is how the two paths drift apart in the first place. Kept
 * here so the file does not grow: `lib-size-baseline.json` holds
 * `api/wishlist.ts` at its ceiling and the ratchet fails a baseline file that
 * grows, so the block CRUD moved *out* rather than in.
 */
export async function call<T>(
  method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH",
  path: string,
  opts: CallOptions = {},
): Promise<T> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const result = await (api as any)[method](path, opts);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const data = (result.data as any) ?? null;
  // A bodyless 2xx where the contract declares a body arrives as `null` and
  // every `return res.data as T` below would ship it to a screen. Guard the
  // one funnel rather than each component — see `emptyBody.ts`.
  if (result.response?.ok)
    assertResponseBody(result.response, result.data, opts.expectBody !== false);
  if (result.response && !result.response.ok) {
    // W39.79. The envelope is in `result.error`, not `result.data` — for any
    // non-2xx `openapi-fetch` leaves `data` undefined and hands back the parsed
    // body separately (dist/index.js:244). So `data?.error?.code` was
    // **always** undefined, and every wishlist failure answered
    // `WISHLIST_ERROR` / `WISHLIST_REQUEST_FAILED` no matter what the server
    // said. The W39.7 pass fixed the *fallback's* wording; this line is why the
    // server's own code and message never reached the screen in the first place,
    // and why every `err.code`-dispatched copy mapper downstream was dead.
    const envelope = ((result as { error?: unknown }).error ?? null) as {
      error?: { code?: string; message?: string };
    } | null;
    const errorCode = envelope?.error?.code ?? "WISHLIST_ERROR";
    // No API path, no HTTP verb, no English. This string is what `Wishes.tsx`
    // renders as the error screen's description, and the branch it guards is
    // real: a 502 from the reverse proxy answers with an HTML body, so
    // `error.message` is absent and this fires. It used to read
    // `Wishlist ${method} ${path} failed`, which put
    // "Wishlist GET /api/v1/wishes failed" under the Chinese title 讀取心願失敗
    // on a real 393×852 render.
    const message = envelope?.error?.message ?? WISHLIST_REQUEST_FAILED;
    // Argument order is `(status, code, message)` — see `ApiError` in
    // `./pair`. This line read `(errorCode, message, status)`, which transposed
    // all three fields for every error this module raises: `.status` held a
    // string, `.code` held the prose, and `.message` held the bare number. The
    // visible damage was `Wishes.tsx` rendering `error.message` as the error
    // screen's description, so a failed wishlist read showed "404" on a
    // Traditional-Chinese page; the rest was that every `err.code`-dispatched
    // copy mapper missed. It is the only one of ~45 `new ApiError` call sites
    // in the codebase with the order wrong.
    throw new ApiError(result.response.status, errorCode, message);
  }
  return data as T;
}

// --- Locked enums (per DATA_MODEL §2) ------------------------------------

export const WISH_CATEGORIES = [
  "general",
  "savings",
  "travel",
  "event",
  "purchase",
  "bucket_list",
  "custom",
] as const;
export type WishCategory = (typeof WISH_CATEGORIES)[number];

export const WISH_STATUSES = ["pending", "in_progress", "completed", "abandoned"] as const;
export type WishStatus = (typeof WISH_STATUSES)[number];

export const WISH_PRIORITIES = ["must", "want", "maybe"] as const;
export type WishPriority = (typeof WISH_PRIORITIES)[number];

export const WISH_VISIBILITIES = ["shared", "private"] as const;
export type WishVisibility = (typeof WISH_VISIBILITIES)[number];

// --- Domain types --------------------------------------------------------

export interface WishSummary {
  id: string;
  name: string;
  category: WishCategory;
  status: WishStatus;
  priority: WishPriority;
  target_date: string | null; // ISO YYYY-MM-DD
  visibility: WishVisibility;
  cover_album_item_id: string | null;
  /**
   * W49.0 P2-4. `delete_wish` is open to either partner; `restore_wish` is
   * author-only. Without this the client could only offer a 復原 button that
   * fails for exactly the people most likely to press it — the one who deleted
   * someone else's wish.
   */
  created_by: string;
  progress_percent: number;
  days_until: number | null;
  completed_at: string | null;
}

export interface WishBlockOut {
  id: string;
  wish_id: string;
  block_type: "progress" | "checklist" | "budget" | "photo" | "link" | "notes";
  position: number;
  title: string;
  data: Record<string, unknown>;
  progress_summary: Record<string, unknown> | null;
  checklist_progress: Record<string, unknown> | null;
  budget_summary: Record<string, unknown> | null;
}

/** Alias onto the contract — see `schema.ts`. Not a local copy. */
export type LinkedDiarySummary = SchemaLinkedDiarySummary;

export interface WishOut {
  id: string;
  pair_id: string;
  name: string;
  category: WishCategory;
  status: WishStatus;
  priority: WishPriority;
  target_date: string | null;
  created_by: string;
  visibility: WishVisibility;
  cover_album_item_id: string | null;
  completion_mode: "manual" | "aggregate";
  manual_percent: number | null;
  completion_sources: string[];
  progress_percent: number;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
  blocks: WishBlockOut[];
  linked_diaries: LinkedDiarySummary[];
}

export interface WishListResponse {
  items: WishSummary[];
  next_offset: number | null;
  total: number;
}

// --- Request payloads ----------------------------------------------------

export interface WishCreate {
  name: string;
  category?: WishCategory;
  priority?: WishPriority;
  target_date?: string | null;
  cover_album_item_id?: string | null;
  visibility?: WishVisibility;
  completion_mode?: "manual" | "aggregate";
  manual_percent?: number | null;
}

export interface WishUpdate {
  name?: string;
  category?: WishCategory;
  priority?: WishPriority;
  target_date?: string | null;
  cover_album_item_id?: string | null;
  visibility?: WishVisibility;
  completion_mode?: "manual" | "aggregate";
  manual_percent?: number | null;
  completion_sources?: string[];
  status?: WishStatus;
}

export interface ArchiveListResponse {
  items: WishSummary[];
  next_offset: number | null;
  total: number;
}

export interface ArchiveFilters {
  /** Calendar year of `completed_at` (e.g. 2026). Multi-select OR. */
  years?: number[];
  category?: WishCategory[];
  priority?: WishPriority[];
  /** Min + max percent (0-100). Inclusive bounds. */
  minPercent?: number;
  maxPercent?: number;
  /** ISO YYYY-MM-DD bounds on `completed_at`. */
  completedFrom?: string;
  completedTo?: string;
  limit?: number;
  offset?: number;
}

/**
 * List archived wishes (`status='completed' AND deleted_at IS NULL` per
 * DATA_MODEL §8.8 + DESIGN §5.8 invariant). Sorted by `completed_at DESC`.
 * Default limit 20 per the spec's "每頁 20 條" pagination.
 */
export async function listArchiveWishes(
  filters: ArchiveFilters = {},
): Promise<ArchiveListResponse> {
  return call<ArchiveListResponse>("GET", "/api/v1/wishes/archive", {
    params: {
      query: {
        years: filters.years,
        category: filters.category,
        priority: filters.priority,
        min_percent: filters.minPercent,
        max_percent: filters.maxPercent,
        completed_from: filters.completedFrom,
        completed_to: filters.completedTo,
        limit: filters.limit ?? 20,
        offset: filters.offset ?? 0,
      } as Record<string, unknown>,
    },
  });
}

// --- List filter shape -------------------------------------------------

export interface WishListFilters {
  /** CSV per backend API_REFERENCE §3.1 (e.g. `"pending,in_progress"`). */
  status?: string;
  category?: string;
  priority?: string;
  visibility?: WishVisibility | "all";
  /** days — wish with target_date <= today+N */
  target_date_within?: number;
  created_by?: string;
  limit?: number;
  offset?: number;
}

// --- Client -------------------------------------------------------------

/**
 * List wishes in the caller's active pair.
 *
 * Defaults: limit 50, offset 0, visibility="all" (returns both parties'
 * shared wishes + the caller's own private wishes per DATA_MODEL §3.5).
 */
export async function listWishes(filters: WishListFilters = {}): Promise<WishListResponse> {
  return call<WishListResponse>("GET", "/api/v1/wishes", {
    // openapi-fetch expects `Record<string, unknown>`; our typed
    // WishListFilters lacks an index signature. Spread into a dict so
    // the cast can stay loose without losing type safety at the
    // call site. Nested under `query` — openapi-fetch reads query
    // parameters from `params.query` (see diary.ts CallOptions).
    params: { query: { ...filters } as Record<string, unknown> },
  });
}

export async function getWish(id: string): Promise<WishOut> {
  return call<WishOut>("GET", `/api/v1/wishes/${id}`);
}

export async function createWish(payload: WishCreate): Promise<WishOut> {
  return call<WishOut>("POST", "/api/v1/wishes", { body: payload });
}

export async function patchWish(id: string, payload: WishUpdate): Promise<WishOut> {
  return call<WishOut>("PATCH", `/api/v1/wishes/${id}`, { body: payload });
}

/**
 * Soft-delete a wish. Per DATA_MODEL §8.8: sets `deleted_at` + cascades
 * the calendar mirror (per listener in W33.5 once calendar module ships).
 *
 * Reversible: the author can call `restoreWish` for 7 days
 * (`410 WISH_RESTORE_WINDOW_EXPIRED` past it), then the soft delete is
 * purged for good. Matches `POST /api/v1/diaries/{id}/restore`.
 */
export async function deleteWish(id: string): Promise<void> {
  return call<void>("DELETE", `/api/v1/wishes/${id}`, { expectBody: false });
}

/**
 * `POST /api/v1/wishes/{id}/restore` — undo a soft delete.
 *
 * Author-only, 30-day window (`410 WISH_RESTORE_WINDOW_EXPIRED` past it).
 * W39.54: the window was 7 and is now 30, matching the retention job that
 * hard-deletes the row, so the window a person can act in and the window the
 * data survives are one number.
 * Mirrors `restoreDiary` / `restoreAnniversary`. `call` already throws the
 * `AppError` envelope, so this stays a pass-through like `getWish`.
 */
export async function restoreWish(id: string): Promise<WishOut> {
  return call<WishOut>("POST", `/api/v1/wishes/${id}/restore`);
}

// --- Trash (W39.54) ---------------------------------------------------------
//
// A soft delete was only ever recoverable by id, and nothing listed the ids, so
// 「restorable for N days」 was true to nobody but the person who wrote it down.
// These three functions are the difference between that and a trash bin.

/**
 * Alias onto the contract — see `schema.ts`. Not a local copy.
 *
 * `days_left` stays server-computed on purpose (ADR-0124): a client countdown
 * against a hard-coded 30 would disagree with the cron by however long the tab
 * had been open. The contract's own JSDoc carries the rest of the rationale.
 */
export type WishTrashItem = SchemaWishTrashItem;

/** `GET /api/v1/wishes/trash` — note this literal path must stay above
 * `/wishes/{wish_id}` on the server, or the latter swallows it (see
 * `backend/tests/test_route_shadowing.py`). */
export async function listWishTrash(): Promise<WishTrashItem[]> {
  const data = await call<{ items: WishTrashItem[] } | null>("GET", "/api/v1/wishes/trash");
  return data?.items ?? [];
}

/** `DELETE /api/v1/wishes/{id}/permanent` — the irreversible one. */
export async function purgeWish(id: string): Promise<void> {
  await call<null>("DELETE", `/api/v1/wishes/${id}/permanent`, { expectBody: false });
}

/**
 * Force-recompute the wish's `progress_percent` per the DATA_MODEL §5.4
 * algorithm. Used by the kanban after-drag `auto-complete` flow and by the
 * layout editor after reorder / block delete.
 */
export async function recomputeProgress(id: string): Promise<{
  percent: number;
  mode: "manual" | "aggregate";
  sources: { source: string; percent: number }[];
}> {
  return call("POST", `/api/v1/wishes/${id}/recompute-progress`);
}

// --- Stats + Upcoming (per DESIGN §5.5 + §6.6) ------------------------

export interface WishStatsResponse {
  total: number;
  completed: number;
  in_progress: number;
  pending: number;
  abandoned: number;
  completion_rate: number;
  upcoming: WishSummary[];
  current_year_total: number;
  current_year_completed: number;
  current_year_completion_rate: number;
}

export interface UpcomingWishSummary {
  id: string;
  name: string;
  target_date: string | null;
  priority: "must" | "want" | "maybe";
  days_until: number | null;
  category: string;
  cover_url: string | null;
}

/**
 * 刻意手寫：contract 的 `UpcomingWishSummary` 把 `priority` 標成裸 `string`，
 * 而 kanban 卡片要用 `"must" | "want" | "maybe"`；而 `getUpcomingWishes` 會
 * 直接把結果解開成本地 `UpcomingWishSummary[]`。見 `schema-parity.test.ts`。
 */
export interface UpcomingResponse {
  upcoming: UpcomingWishSummary[];
}

export async function getWishStats(): Promise<WishStatsResponse> {
  return call<WishStatsResponse>("GET", "/api/v1/wishes/stats");
}

export async function getUpcomingWishes(
  args: { days?: number; limit?: number } = {},
): Promise<UpcomingWishSummary[]> {
  const params: Record<string, unknown> = {};
  if (args.days) params.days = args.days;
  if (args.limit) params.limit = args.limit;
  // Server returns `{ upcoming: [...] }`; unwrap to a flat array.
  const wrapped = await call<UpcomingResponse>("GET", "/api/v1/wishes/upcoming", {
    params: { query: params },
  });
  return wrapped.upcoming;
}

// --- Block sub-resources ----------------------------------------------
//
// The four block-scoped sub-resource sections (progress log · checklist ·
// links · photos) moved to `./wishBlocks` — same reason `reorderBlocks` did:
// this file is pinned in `lib-size-baseline.json` at its ceiling and the
// ratchet fails a baseline file that grows. They are keyed by wishId+blockId,
// which is that file's key. `reorderBlocks` moved here for the same reason.
// --- Sub-table reorder (per API_REFERENCE §5.5) ----------------------
//
// `reorderBlocks` moved to `./wishBlocks` with the rest of the block CRUD — it
// was the odd one out, a block endpoint living 60 lines below the photo
// reorder. That also keeps this file inside its recorded size: it is pinned in
// `lib-size-baseline.json` and the ratchet fails a baselined file that grows.

// --- W35 sub-resource CRUD (per W35-WISH-V2-PARITY-ENHANCEMENT.md) ----
//
// 17 endpoints (5 sub-resources × CRUD + extras) + 1 audit-log read
// endpoint. Schemas mirror backend `app/modules/wishlist/schemas.py`.
//
// W35.2 frontend polish ships the typed wrappers here so the 4 new
// WishDetail panels + WishEventTimeline can call them with full
// type safety. The openapi.yaml regen + `pnpm gen:api` are still
// deferred (W35.1 §3 follow-up); until then, these are hand-written
// and authoritative.

// --- Common -----------------------------------------------------------

/**
 * 刻意手寫：contract 的 `avatar_url` 是 optional，這裡要求一定有值。alias 只會
 * 把唯一消費方（`WishEventOut.user`）的讀取白白放寬成 `| undefined` —— shape
 * 並非完全相同，所以不該進 `schema.ts`。見 `schema-parity.test.ts`。
 */
export interface UserBrief {
  id: string;
  display_name: string;
  avatar_url: string | null;
}

// --- W35 §3.1 WishReminder (4 endpoints) -------------------------------

export interface WishReminderOut {
  id: string;
  wish_id: string;
  remind_at: string;
  lead_unit: "day" | "hour";
  lead_value: number;
  channel: "app" | "email" | "telegram";
  note: string | null;
  triggered_at: string | null;
  created_by: string;
  created_at: string;
}

export interface WishReminderCreate {
  remind_at: string;
  lead_unit?: "day" | "hour";
  lead_value?: number;
  channel?: "app" | "email" | "telegram";
  note?: string | null;
}

export interface WishReminderUpdate {
  remind_at?: string;
  lead_unit?: "day" | "hour";
  lead_value?: number;
  channel?: "app" | "email" | "telegram";
  note?: string | null;
}

export async function listReminders(wishId: string): Promise<WishReminderOut[]> {
  return call<WishReminderOut[]>("GET", `/api/v1/wishes/${wishId}/reminders`);
}

export async function createReminder(
  wishId: string,
  payload: WishReminderCreate,
): Promise<WishReminderOut> {
  return call<WishReminderOut>("POST", `/api/v1/wishes/${wishId}/reminders`, { body: payload });
}

export async function patchReminder(
  wishId: string,
  reminderId: string,
  payload: WishReminderUpdate,
): Promise<WishReminderOut> {
  return call<WishReminderOut>("PATCH", `/api/v1/wishes/${wishId}/reminders/${reminderId}`, {
    body: payload,
  });
}

export async function deleteReminder(wishId: string, reminderId: string): Promise<void> {
  return call<void>("DELETE", `/api/v1/wishes/${wishId}/reminders/${reminderId}`, {
    expectBody: false,
  });
}

// --- W35 §3.2 WishChatMessage (4 endpoints) -------------------------

export interface WishChatMessageAuthor {
  id: string;
  display_name: string;
  avatar_url: string | null;
}

export interface WishChatMessageOut {
  id: string;
  wish_id: string;
  user_id: string;
  body: string;
  edited_at: string | null;
  created_at: string;
  sender: WishChatMessageAuthor | null;
}

export interface WishChatMessageListResponse {
  items: WishChatMessageOut[];
  total: number;
  next_offset: number | null;
}

/** Alias onto the contract — see `schema.ts`. Not a local copy. */
export type WishChatMessageCreate = SchemaWishChatMessageCreate;

/** Alias onto the contract — see `schema.ts`. Not a local copy. */
export type WishChatMessageUpdate = SchemaWishChatMessageUpdate;

export async function listChatMessages(
  wishId: string,
  args: { limit?: number; offset?: number } = {},
): Promise<WishChatMessageListResponse> {
  const params: Record<string, unknown> = {};
  if (args.limit) params.limit = args.limit;
  if (args.offset) params.offset = args.offset;
  return call<WishChatMessageListResponse>("GET", `/api/v1/wishes/${wishId}/messages`, {
    params: { query: params },
  });
}

export async function createChatMessage(
  wishId: string,
  payload: WishChatMessageCreate,
): Promise<WishChatMessageOut> {
  return call<WishChatMessageOut>("POST", `/api/v1/wishes/${wishId}/messages`, { body: payload });
}

export async function patchChatMessage(
  wishId: string,
  messageId: string,
  payload: WishChatMessageUpdate,
): Promise<WishChatMessageOut> {
  return call<WishChatMessageOut>("PATCH", `/api/v1/wishes/${wishId}/messages/${messageId}`, {
    body: payload,
  });
}

export async function deleteChatMessage(wishId: string, messageId: string): Promise<void> {
  return call<void>("DELETE", `/api/v1/wishes/${wishId}/messages/${messageId}`, {
    expectBody: false,
  });
}

// --- W35 §3.3 WishLinkMetadata (2 endpoints) -----------------------

export interface WishLinkMetadataOut {
  link_item_id: string;
  og_title: string | null;
  og_description: string | null;
  og_image_url: string | null;
  og_site_name: string | null;
  fetch_status: "pending" | "success" | "failed";
  error_message: string | null;
  fetched_at: string;
}

export interface WishLinkMetadataUpdate {
  og_title?: string | null;
  og_description?: string | null;
  og_image_url?: string | null;
  og_site_name?: string | null;
  fetch_status?: "pending" | "success" | "failed";
  error_message?: string | null;
}

export async function getLinkMetadata(
  wishId: string,
  blockId: string,
  linkId: string,
): Promise<WishLinkMetadataOut | null> {
  return call<WishLinkMetadataOut | null>(
    "GET",
    `/api/v1/wishes/${wishId}/blocks/${blockId}/links/${linkId}/metadata`,
  );
}

export async function upsertLinkMetadata(
  wishId: string,
  blockId: string,
  linkId: string,
  payload: WishLinkMetadataUpdate,
): Promise<WishLinkMetadataOut> {
  return call<WishLinkMetadataOut>(
    "POST",
    `/api/v1/wishes/${wishId}/blocks/${blockId}/links/${linkId}/fetch-metadata`,
    { body: payload },
  );
}

// --- W35 §3.4 WishTimelineItem (5 endpoints) ----------------------

/** Alias onto the contract — see `schema.ts`. Not a local copy. */
export type WishTimelineItemOut = SchemaWishTimelineItemOut;

/** Alias onto the contract — see `schema.ts`. Not a local copy. */
export type WishTimelineItemCreate = SchemaWishTimelineItemCreate;

export interface WishTimelineItemUpdate {
  milestone?: string;
  target_date?: string;
  position?: number | null;
}

export async function listTimeline(wishId: string): Promise<WishTimelineItemOut[]> {
  return call<WishTimelineItemOut[]>("GET", `/api/v1/wishes/${wishId}/timeline`);
}

export async function createTimelineItem(
  wishId: string,
  payload: WishTimelineItemCreate,
): Promise<WishTimelineItemOut> {
  return call<WishTimelineItemOut>("POST", `/api/v1/wishes/${wishId}/timeline`, { body: payload });
}

export async function patchTimelineItem(
  wishId: string,
  timelineId: string,
  payload: WishTimelineItemUpdate,
): Promise<WishTimelineItemOut> {
  return call<WishTimelineItemOut>("PATCH", `/api/v1/wishes/${wishId}/timeline/${timelineId}`, {
    body: payload,
  });
}

export async function deleteTimelineItem(wishId: string, timelineId: string): Promise<void> {
  return call<void>("DELETE", `/api/v1/wishes/${wishId}/timeline/${timelineId}`, {
    expectBody: false,
  });
}

export async function completeTimelineItem(
  wishId: string,
  timelineId: string,
): Promise<WishTimelineItemOut> {
  return call<WishTimelineItemOut>(
    "POST",
    `/api/v1/wishes/${wishId}/timeline/${timelineId}/complete`,
  );
}

// --- W35 §4 Wish.phases (5 endpoints) ----------------------------

export interface WishPhaseOut {
  phase_id: string;
  title: string;
  percent: number;
  completed_at: string | null;
}

export interface WishPhaseCreate {
  title: string;
  percent?: number;
  position?: number | null;
}

export interface WishPhaseUpdate {
  title?: string;
  percent?: number;
  position?: number | null;
}

export async function listPhases(wishId: string): Promise<WishPhaseOut[]> {
  return call<WishPhaseOut[]>("GET", `/api/v1/wishes/${wishId}/phases`);
}

export async function createPhase(wishId: string, payload: WishPhaseCreate): Promise<WishPhaseOut> {
  return call<WishPhaseOut>("POST", `/api/v1/wishes/${wishId}/phases`, { body: payload });
}

export async function patchPhase(
  wishId: string,
  phaseId: string,
  payload: WishPhaseUpdate,
): Promise<WishPhaseOut> {
  return call<WishPhaseOut>("PATCH", `/api/v1/wishes/${wishId}/phases/${phaseId}`, {
    body: payload,
  });
}

export async function deletePhase(wishId: string, phaseId: string): Promise<void> {
  return call<void>("DELETE", `/api/v1/wishes/${wishId}/phases/${phaseId}`, { expectBody: false });
}

export async function completePhase(wishId: string, phaseId: string): Promise<WishPhaseOut> {
  return call<WishPhaseOut>("POST", `/api/v1/wishes/${wishId}/phases/${phaseId}/complete`);
}

// --- W35 §2 WishEvent audit log (1 endpoint) ----------------------

export interface WishEventOut {
  id: string;
  action: string;
  payload: Record<string, unknown>;
  user: UserBrief | null;
  created_at: string;
}

/**
 * 刻意手寫：contract 的 `WishEventOut` 把 `payload` 與 `user` 標成 optional，
 * 而稽核記錄會直接 render `user.display_name`。見 `schema-parity.test.ts`。
 */
export interface WishEventListResponse {
  items: WishEventOut[];
  total: number;
}

export async function listWishEvents(
  wishId: string,
  args: { limit?: number } = {},
): Promise<WishEventListResponse> {
  const params: Record<string, unknown> = {};
  if (args.limit) params.limit = args.limit;
  return call<WishEventListResponse>("GET", `/api/v1/wishes/${wishId}/events`, {
    params: { query: params },
  });
}
